import { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import { Close, CloudUpload, InsertDriveFile } from '@mui/icons-material';
import { useUploadCsvProspectsMutation } from '../store/hooks';

interface ProspectsUploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_FILE_TYPES = ['.csv'];
const ALLOWED_MIME_TYPES = ['text/csv', 'application/csv', 'application/vnd.ms-excel'];

export function ProspectsUploadModal({
  open,
  onClose,
  onSuccess,
}: ProspectsUploadModalProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    total_requested: number;
    errors?: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadFile, { isLoading }] = useUploadCsvProspectsMutation();

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return `File size exceeds 50MB limit. Selected file is ${(file.size / (1024 * 1024)).toFixed(2)}MB`;
    }

    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_FILE_TYPES.includes(fileExtension)) {
      return 'Invalid file type. Only CSV files are allowed.';
    }

    if (!ALLOWED_MIME_TYPES.includes(file.type) && file.type !== '') {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        return 'Invalid file type. Only CSV files are allowed.';
      }
    }

    return null;
  };

  const handleFileSelect = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }

    setError(null);
    setSelectedFile(file);
    setUploadProgress(0);
    setUploadResult(null);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  }, [handleFileSelect]);

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploadProgress(25);

      const result = await uploadFile({
        file: selectedFile,
      }).unwrap();

      setUploadProgress(100);
      setUploadResult({
        total_requested: result.total_requested,
        errors: result.errors,
      });

      // Success - close after short delay
      setTimeout(() => {
        onSuccess();
        handleClose();
      }, 1500);
    } catch (error: any) {
      let errorMessage = 'Failed to upload file. Please try again.';

      if (error?.data?.error) {
        errorMessage = error.data.error;
        if (error?.data?.details?.message) {
          errorMessage += `: ${error.data.details.message}`;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.data?.message) {
        errorMessage = error.data.message;
      } else if (error?.data?.detail) {
        errorMessage = error.data.detail;
      } else if (typeof error?.data === 'string') {
        errorMessage = error.data;
      } else if (error?.status === 400) {
        errorMessage = 'Invalid request. Please check the file format and try again.';
      } else if (error?.status === 413) {
        errorMessage = 'File is too large. Maximum file size is 50MB.';
      } else if (error?.status === 422) {
        errorMessage = 'Invalid data format. Please ensure the file is a valid CSV.';
      }

      setError(errorMessage);
      setUploadProgress(0);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setError(null);
    setUploadProgress(0);
    setUploadResult(null);
    setIsDragging(false);
    onClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"sm"}>
      <DialogTitle>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6">Upload Prospect CSV</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {uploadResult && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully queued {uploadResult.total_requested} prospects for enrichment.
              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {uploadResult.errors.length} row(s) had errors.
                </Typography>
              )}
            </Alert>
          )}

          <Box
            sx={{
              border: isDragging ? '2px dashed #1976d2' : '2px dashed #ccc',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragging ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                borderColor: '#1976d2',
                bgcolor: 'rgba(25, 118, 210, 0.02)',
              },
            }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />

            {selectedFile ? (
              <>
                <InsertDriveFile sx={{ fontSize: 48, color: '#1976d2', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFile.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(selectedFile.size)}
                </Typography>
                {uploadProgress > 0 && (
                  <Box sx={{ mt: 2, width: '100%' }}>
                    <LinearProgress variant="determinate" value={uploadProgress} />
                  </Box>
                )}
              </>
            ) : (
              <>
                <CloudUpload sx={{ fontSize: 48, color: '#999', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Drag & Drop or Click to Upload
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload CSV file (up to 50MB)
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Expected CSV Columns:
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Required:</strong> Organization, First Name, Last Name, Email
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Optional:</strong> Title, Phone Number
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              Note: Enrichment runs asynchronously after upload. Large uploads may take 10-30 minutes to fully process.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={!selectedFile || isLoading}
          startIcon={isLoading ? undefined : <CloudUpload />}
        >
          {isLoading ? 'Uploading...' : 'Upload CSV'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
